/**
 * 社員の分布の形（DB04）。
 *
 * 正典: apps/web/docs/新方針20260821/GB組織_説明文DB_v1_23.json の DB04
 *
 * 【判定】象限比率と中心付近比率を、回答者数別の偶然基準と比べる。
 *   ① 回答7名未満          → 04-小規模（傾向として読まない）
 *   ② 対角二極（A側とC側 / B側とD側の両端に分かれる） → 04-両極AC / 04-両極BD
 *   ③ 中心付近比率 ≥ 基準   → 04-中央
 *   ④ 最大象限比率 ≥ 基準   → 04-集中A〜D
 *   ⑤ どれにも当てはまらない → 04-均等
 *
 * 【04-RULE-優先】中央集中と分極(DB12)が同時に成立したら分極を優先し、
 *  中央集中は表示しない。「尖りが少なくまとまりやすい」と「持ち味が二分している」が
 *  同じレポートに並ぶのを防ぐため。
 *
 * 【偶然基準について（重要）】
 *  DB04 が示す値は自前のシミュレーションで再現できなかった。
 *  ・ランダム回答モデル（DB05・DB14 の値はこれで完全に一致した）
 *      象限95%点 n=12:50% / n=22:41% / n=50:32%（DBは 58/50/42）
 *  ・到達可能空間から一様抽出するモデル（DB12 の注記が示す作り方）
 *      象限95%点 n=12:50% / n=22:45% / n=50:40%
 *  いずれもDB記載値より低い。DB04自身が「パイロットで確定します」としているため、
 *  ここではDB記載値をそのまま採用する。DB値のほうが高い＝判定が出にくい側なので、
 *  誤って「集中している」と言ってしまう危険は小さい。廣瀬様への確認候補。
 */
import type { AxisKey } from './gb74Report';
import { findOrgText, type OrgTextEntry } from './orgTextsV3';
import {
  DIST_LEANING_TEXT,
  DIST_NEUTRAL_TEXT,
  DIST_THREE_AXIS_TEXT,
  DIST_THREE_QUADRANT_TEXT,
  leaningKeyOf,
} from './orgDistributionCopyV3';
import type { OrgShapeResultV3 } from './orgShapeV3';

/** 中心付近とみなす範囲（|x| と |y| がこれ以下）。DB04は半径3。 */
export const CENTER_RADIUS = 3;

/** 判定を行う最小人数（04-小規模）。 */
export const DIST_MIN_MEMBERS = 7;

/**
 * 象限比率の偶然基準（04-基準）。[回答者数, 95%点]。
 * DB記載の3点。中間は線形補間し、範囲外は端で頭打ちにする。
 */
const QUADRANT_NULL: ReadonlyArray<readonly [number, number]> = [
  [12, 0.58],
  [22, 0.5],
  [50, 0.42],
];

/**
 * 中心付近比率の偶然基準。DBは n=22 の 55% しか示していないため、
 * 象限基準の人数による下がり方をそのまま当てはめて他の人数に広げる。
 */
const CENTER_AT_22 = 0.55;

/** 表の線形補間。 */
function interp(table: ReadonlyArray<readonly [number, number]>, n: number): number {
  if (n <= table[0][0]) return table[0][1];
  const last = table[table.length - 1];
  if (n >= last[0]) return last[1];
  for (let i = 1; i < table.length; i++) {
    if (n <= table[i][0]) {
      const [x0, y0] = table[i - 1];
      const [x1, y1] = table[i];
      return y0 + ((y1 - y0) * (n - x0)) / (x1 - x0);
    }
  }
  return last[1];
}

/** 象限比率の偶然基準。 */
export function quadrantThreshold(n: number): number {
  return interp(QUADRANT_NULL, n);
}

/** 中心付近比率の偶然基準。 */
export function centerThreshold(n: number): number {
  return (CENTER_AT_22 * quadrantThreshold(n)) / interp(QUADRANT_NULL, 22);
}

/** 象限ごとの人数。 */
export interface QuadrantCounts {
  A: number;
  B: number;
  C: number;
  D: number;
  center: number;
}

export interface OrgDistributionV3 {
  counts: QuadrantCounts;
  /** 最も人数の多い象限とその比率 */
  topQuadrant: AxisKey;
  topRate: number;
  /** 中心付近の比率 */
  centerRate: number;
  /** 対角ペアで両端に分かれているか */
  bipolar: 'AC' | 'BD' | null;
  /**
   * 隣り合う2象限に寄っているか（CD＝右寄り、AB＝左寄り、AD＝上寄り、BC＝下寄り）。
   * DB04にはこの区分の文言が無いため、判定結果を本文の組み立てに使う。
   */
  leaning: [AxisKey, AxisKey] | null;
  /** 4象限が実際に均等か（04-均等 を出してよいか） */
  even: boolean;
  /** 引き当てた文言 */
  text?: OrgTextEntry;
  memberCount: number;
}

/**
 * 形の判定結果から DB04 のエントリIDを決める。
 *
 * 判定そのものは orgShapeV3 に一本化した（型と分布で土台を1つにするため）。
 * ここは「形 → 分布の文言」の対応づけだけを持つ。
 */
function distTextId(r: OrgShapeResultV3): string {
  // 形が判定できない人数（15名未満）は、分布も断定しない。
  // 04-小規模「母数が小さいため、傾向ではなく個々の持ち味の確認材料として」を使う。
  if (r.shape === null) return '04-小規模';
  if (r.thinAxis) return DIST_THREE_AXIS_TEXT[r.thinAxis].id;
  if (r.emptyQuadrant) return DIST_THREE_QUADRANT_TEXT[r.emptyQuadrant].id;
  switch (r.shape) {
    case '中心集中型':
      return '04-中央';
    case '二極型':
      return `04-両極${r.diagonal ? leaningKeyOf(r.diagonal) : 'AC'}`;
    case '同系統型':
      return r.adjacent ? DIST_LEANING_TEXT[leaningKeyOf(r.adjacent)].id : '04-均等';
    case '集中型':
      return `04-集中${r.quadrant ?? 'A'}`;
    case '分散型':
      return '04-均等';
    default:
      // 中間型。「四方に散らばっている」は分散型の言い方なので使わない。
      return DIST_NEUTRAL_TEXT.id;
  }
}

/**
 * 社員の分布の文言を引く。判定は orgShapeV3 で済ませたものを受け取る。
 * 型(1-2)と分布(1-3)を同じ判定結果から作ることで、両者が食い違わないようにしている。
 */
export function buildOrgDistributionV3(shapeResult: OrgShapeResultV3): OrgDistributionV3 {
  const { counts, memberCount: n, rates, centerRate } = shapeResult;
  const AX: AxisKey[] = ['A', 'B', 'C', 'D'];
  const topQuadrant = AX.reduce((a, b) => (counts[a] >= counts[b] ? a : b));
  return {
    counts,
    topQuadrant,
    topRate: rates[topQuadrant],
    centerRate,
    bipolar: shapeResult.diagonal ? (leaningKeyOf(shapeResult.diagonal) as 'AC' | 'BD') : null,
    leaning: shapeResult.adjacent,
    even: shapeResult.shape === '分散型',
    text: resolveText(distTextId(shapeResult)),
    memberCount: n,
  };
}

function resolveText(id: string): OrgTextEntry | undefined {
  const fromDb = findOrgText('DB04', id);
  if (fromDb) return fromDb;
  const local = [
    ...Object.values(DIST_LEANING_TEXT),
    ...Object.values(DIST_THREE_AXIS_TEXT),
    ...Object.values(DIST_THREE_QUADRANT_TEXT),
    DIST_NEUTRAL_TEXT,
  ].find((t) => t.id === id);
  return local
    ? {
        id: local.id,
        条件: 'DB04に該当区分が無いため補った区分',
        見出し: local.見出し,
        本文: local.本文,
      }
    : undefined;
}

/** 軸の日本語名（本文の組み立てに使う）。 */
export const AXIS_PERSON_LABEL: Record<AxisKey, string> = {
  A: '論理から考える方',
  B: '着実に進める方',
  C: '人から考える方',
  D: '新しさを求める方',
};
